import cn from 'classnames'
import React, {
  FC, useEffect, useMemo, useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import moment from 'moment'

import style from '@src/pages/ChangeTariff/ChangeTariff.module.scss'
import { IndividualsPanel } from '@src/components/IndividualsPanel/IndividualsPanel'
import { MyTariffPlan } from '@src/components/MyTariffPlan/MyTariffPlan'
import { SubscribeBlock } from '@src/components/SubscribeBlock/SubscribeBlock'
import { Footer } from '@src/components/Footer/Footer'
import { AppDispatch } from '@src/store/store'
import { ReturnBtn } from '@src/components/Button/ReturnBtn'
import { getAvailableTariffs, selectTariff } from '@src/store/ducks/tariffs/thunks'
import { AvailableTariff, AvailableTariffGoodType } from '@src/store/ducks/tariffs/types'
import { selectUserInfo, selectUserServices } from '@src/store/ducks/user/selectors'
import { resetTariffs } from '@src/store/ducks/tariffs/reducer'
import {
  selectTariffsAvailableTariff,
  selectTariffsIsLoading,
  selectTariffsIsLoadingChangeTariff, selectTariffsIsSuccessChangeTariff,
} from '@src/store/ducks/tariffs/selectors'
import { Modal } from '@src/components/common/Modal/Modal'
import { Button } from '@src/components/Button/Button'
import { Preloader } from '@src/components/common/Preloader/Preloader'
import { setPageTitle } from '@src/store/ducks/app/reducer'
import { selectAppMessages } from '@src/store/ducks/app/selectors'
import { parsPrice } from '@src/lib/utils'

type ChangeTariffProps = {
  isHome?: boolean
  serviceId?: number
}

export const ChangeTariff: FC<ChangeTariffProps> = ({ isHome, serviceId }) => {
  const params = useParams<{ serviceId: string }>()
  const dispatch = useDispatch<AppDispatch>()
  const { tariff, newTariff } = useSelector(selectUserServices)
  const availableTariff = useSelector(selectTariffsAvailableTariff)
  const isLoading = useSelector(selectTariffsIsLoading)
  const isLoadingChangeTariff = useSelector(selectTariffsIsLoadingChangeTariff)
  const isSuccessChangeTariff = useSelector(selectTariffsIsSuccessChangeTariff)
  const messages = useSelector(selectAppMessages)
  const [select, setSelect] = useState<AvailableTariff | null>(null)
  const user = useSelector(selectUserInfo)
  const cServiceId = useMemo(() => {
    if (serviceId) {
      return serviceId
    }
    return params.serviceId
  }, [params.serviceId, serviceId])

  useEffect(() => { dispatch(setPageTitle(user.isSubscriber ? 'Смена подписки' : 'Смена тарифного плана')) }, [dispatch])

  useEffect(() => {
    if (tariff) {
      dispatch(getAvailableTariffs(AvailableTariffGoodType.TARIFF, +cServiceId))
    }
  }, [dispatch, cServiceId, tariff])

  useEffect(() => () => {
    dispatch(resetTariffs())
  }, [dispatch])

  return (
    <>
      <div>
        {!isHome && (
          <IndividualsPanel />
        )}

        <div className={cn(style.changeTariffContainer, 'container')}>
          {!isHome && (
            <>
              <ReturnBtn type="secondary">Вернуться</ReturnBtn>
              <h4 className={style.title}>
                {user.isSubscriber ? 'Ваша действующая подписка' : 'Ваш действующий тарифный план'}
              </h4>
              <MyTariffPlan serviceId={+cServiceId} />
              <h1>{user.isSubscriber ? 'Смена подписки' : 'Смена тарифного плана'}</h1>
            </>
          )}

          {isHome && (
            <h2 className={style.sTitleMain}>Доступные подписки</h2>
          )}
          <Preloader loading={isLoading} center>
            {availableTariff.map((el) => (
              <div key={el.title} className={style.block}>
                <div className={style.blockTitle}>{el.title}</div>
                {el.data.map((subscribe: any) => (
                  <>
                    {isHome ? (
                      <div key={subscribe.goodId} className={style.sTariff}>
                        {subscribe.lkImage && (
                          <img
                            className={style.sImage}
                            src={`https://cosmos-telecom.by${subscribe.lkImage}`}
                            alt={subscribe.goodName}
                          />
                        )}
                        <div className={style.sContent}>
                          <div className={style.sTitle}>
                            {subscribe.goodName}
                          </div>
                          <div className={style.sDesc} dangerouslySetInnerHTML={{ __html: subscribe.lkDescription }} />
                          <div className={style.sActions}>
                            <div className={style.sCost}>
                              Стоимость
                              <span>{parsPrice(subscribe.price)}</span>
                              {' '}
                              р/мес
                            </div>
                            <Button
                              type="primary"
                              onClickFunc={() => setSelect(subscribe)}
                              disabled={moment(tariff[0]?.dateStart, 'DD.MM.YYYY HH:mm:ss').diff(moment(), 'days') === 0}
                            >
                              Подключить
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <SubscribeBlock
                        path={subscribe.goodId}
                        key={subscribe.goodId}
                        btnName="Подключить"
                        className={style.sizeBlock}
                        title={(
                          <div>
                            {subscribe.goodName}
                            {newTariff?.some((item) => item.goodId === subscribe.goodId) && (
                              <span className={style.startDate}>
                                (действует с
                                {' '}
                                {moment(newTariff?.find((item) => item.goodId === subscribe.goodId)?.dateStart, 'DD.MM.YYYY').format('DD.MM.YYYY')}
                                )
                              </span>
                            )}
                          </div>
                        )}
                        cost={subscribe.price}
                        descriptionTariff={subscribe.shortDescription}
                        btn
                        btnDisabled={!!newTariff?.length && newTariff?.some((item) => item.goodTypeId === tariff?.find((tariffItem) => tariffItem.serviceId === +cServiceId)?.goodTypeId)}
                        btnClick={() => setSelect(subscribe)}
                      />
                    )}
                  </>
                ))}
              </div>
            ))}
          </Preloader>
        </div>
        {!isHome && (
          <Footer />
        )}
      </div>
      <Modal isOpen={!!select} close={() => setSelect(null)}>
        {isSuccessChangeTariff ? (
          <>
            <div className={style.confirmActions}>
              {messages.successChangeTariffTitle}
            </div>
            <p>{messages?.successChangeTariffDescription?.replace('$tariff', select?.goodName || '')}</p>
          </>
        ) : (
          <>
            <div className={style.confirmActions}>
              {messages.changeTariffTitle}
            </div>
            <p>
              {messages?.changeTariffDescription?.replace('$tariff', select?.goodName || '')}
            </p>
            <div className={style.actions}>
              {select && (
                <Button
                  className={style.btnSize}
                  loading={isLoadingChangeTariff}
                  onClickFunc={() => dispatch(selectTariff(select.goodId, +cServiceId))}
                >
                  Подтвердить
                </Button>
              )}
              <Button
                className={style.btnSize}
                disabled={isLoadingChangeTariff}
                type="textSecondary"
                onClickFunc={() => setSelect(null)}
              >
                Отменить
              </Button>
            </div>
          </>
        )}
      </Modal>
    </>
  )
}
